import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { FetchPolicy } from '@apollo/client';
import { ImagePreset, Product, PurchaseLimitReached, useProduct, AllowSubscriptions } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import { ContentInfoTitle } from '../../components/ContentInfo/ContentInfoTitle';
import { ContentInfoSubtitle } from '../../components/ContentInfo/ContentInfoSubtitle';
import { ContentInfoWrapper } from '../../components/ContentInfo/ContentInfoWrapper';
import { ContentInfoDescription } from '../../components/ContentInfo/ContentInfoDescription';
import { SplitLines } from '../../components/SplitLines';
import { InputPin } from '../../components/Inputs';
import { UserContext } from '../../contexts/UserContext';
import { InputState } from '../../components/InputState';
import { ImageComponent } from '../../components/ImageComponent';
import { purchaseProduct } from '../../api/product';
import { breakpoints } from '../../styles/breakpoints';
import { SettingsContext } from '../../contexts/SettingsContext';
import { HeaderGap } from '../../components/HeaderGap/HeaderGap.styles';
import NotFoundScene from '../404';

const ProductSceneActionsPropsTerms = styled.div`
	font-size: 18px;
	line-height: 1.2;
	margin-bottom: 20px;
`;

const ProductSceneDescription = styled(ContentInfoDescription)`
	margin-bottom: 40px;
	max-height: none;
`;

const ProductSceneAgreeTermsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin-bottom: 20px;

	.checkbox {
		margin-right: 10px;
	}

	span {
		font-size: 18px;
		font-weight: bold;
		line-height: 20px;
	}
`;

const ProductSceneMessage = styled.div`
	font-size: 20px;
	font-weight: bold;
	line-height: 1.2;
	max-width: 400px;
`;

const ProductScenePinWrapper = styled.div`
	margin: 40px auto;
	display: flex;
	flex-direction: column;

	input {
		margin-bottom: 10px;
		min-width: 100%;
	}

	button {
		flex: 0;
	}

	@media ${breakpoints.sm} {
		flex-direction: row;

		input {
			margin-bottom: 0;
			min-width: auto;
		}

		button {
			min-width: 200px;
		}
	}
`;

const ProductScenePinInput = styled(InputPin)`
	max-width: 300px;
	margin-right: 10px;
`;

type ProductSceneActionsProps = {
	product: Product;
};

const ProductSceneActions: React.FC<ProductSceneActionsProps> = ({ product }) => {
	const { t } = useTranslation();
	const router = useRouter();
	const { verifyPin, isLoggedIn } = useContext(UserContext);
	const { settings } = useContext(SettingsContext);

	const purchasing = useRef(false);
	const [termsAgreed, setTermsAgreed] = useState(false);
	const [purchased, setPurchased] = useState(false);
	const [pin, setPin] = useState(null);
	const [pinError, setPinError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	const onPinChange = useCallback((e) => setPin(e.target.value.trim()), []);

	useEffect(() => {
		if (pin && pin.length > 0) {
			setPinError(false);
		}
	}, [pin]);

	const onPurchaseClick = useCallback(async () => {
		if (product && termsAgreed && !purchasing.current) {
			purchasing.current = true;
			setLoading(true);

			try {
				if (AllowSubscriptions[settings?.allowSubscriptions] === AllowSubscriptions.YES_WITH_PIN) {
					const response = await verifyPin(pin);
					if (response.isSuccess()) {
						await purchaseProduct(product.id);
						setPurchased(true);
					} else {
						setPinError(true);
						setPin(null);
					}
				} else {
					await purchaseProduct(product.id);
					setPurchased(true);
				}
			} catch (exception) {
				if (exception instanceof PurchaseLimitReached) {
					setErrorMessage(t('Product.PurchaseLimitReached'));
				} else {
					setErrorMessage(t('Product.PurchaseFailed'));
				}
			}

			setLoading(false);
			purchasing.current = false;
		}
	}, [product, termsAgreed, settings, verifyPin, pin, t]);

	const pinComponent = useMemo(() => {
		if (termsAgreed && AllowSubscriptions[settings?.allowSubscriptions] === AllowSubscriptions.YES_WITH_PIN) {
			return (
				<ProductScenePinInput
					placeholder={pinError ? t('Product.PinError') : t('Product.Pin')}
					value={pin || ''}
					onChange={onPinChange}
					state={pinError ? InputState.ERROR : InputState.NORMAL}
				/>
			);
		}
		return null;
	}, [onPinChange, pin, settings, t, termsAgreed, pinError]);

	if (!product) return null;

	const { subscribed, successText } = product;

	if (!isLoggedIn) {
		return (
			<ProductSceneMessage>
				<span className={'title'}>
					{`${t('Product.LoginRequired.Part1')} `}
					<Link href={`/login?r=${encodeURIComponent(router.asPath)}`}>{t('Product.LoginRequired.Part2')}</Link>
				</span>
			</ProductSceneMessage>
		);
	}

	if (errorMessage) {
		return <ProductSceneMessage>{errorMessage}</ProductSceneMessage>;
	}

	if (subscribed) {
		return <ProductSceneMessage>{t('Product.Subscribed')}</ProductSceneMessage>;
	}

	if (AllowSubscriptions[settings?.allowSubscriptions] === AllowSubscriptions.NO) {
		return <ProductSceneMessage>{t('Product.NotAllowed')}</ProductSceneMessage>;
	}

	if (purchased) {
		return <ProductSceneMessage>{successText}</ProductSceneMessage>;
	}

	if (loading) {
		return <ProductSceneMessage>{t('Product.Loading')}</ProductSceneMessage>;
	}

	return (
		<>
			<h4>{t('Product.Terms')}</h4>
			<ProductSceneActionsPropsTerms>
				<SplitLines>{product?.terms}</SplitLines>
			</ProductSceneActionsPropsTerms>
			<ProductSceneAgreeTermsWrapper>
				<Checkbox checked={termsAgreed} onCheckedChange={setTermsAgreed} />
				<span>{t('Product.AgreeTerms')}</span>
			</ProductSceneAgreeTermsWrapper>
			<ProductScenePinWrapper>
				{pinComponent}
				<Button title={t('Product.Purchase')} subtitle={`${product?.price} kr.`} onClick={onPurchaseClick} />
			</ProductScenePinWrapper>
		</>
	);
};

const queryOptions = {
	fetchPolicy: 'network-only' as FetchPolicy,
};

const ProductSceneContent: React.FC<{ productId: number }> = ({ productId }) => {
	const { product } = useProduct(productId, queryOptions);

	const backdropComponent = useMemo(() => {
		let url = null;

		if (product) {
			const imageParams = { presets: [ImagePreset.BACKDROP, ImagePreset.BACKDROP_OVERLAY] };
			url = product.getBackdropImageUrl(imageParams);

			if (!url) {
				url = product.getModuleImageUrl(imageParams);
			}
		} else {
			return <NotFoundScene />;
		}

		if (url) {
			return <ImageComponent className={'background'} images={[url]} />;
		}

		return null;
	}, [product]);

	return (
		<HeaderGap>
			{backdropComponent}
			<ContentInfoWrapper>
				<ContentInfoTitle>
					<h1>{product?.title}</h1>
				</ContentInfoTitle>
				{product?.subtitle && <ContentInfoSubtitle>{product?.subtitle}</ContentInfoSubtitle>}
				<ProductSceneDescription>
					<SplitLines>{product?.description}</SplitLines>
				</ProductSceneDescription>
				<ProductSceneActions product={product} />
			</ContentInfoWrapper>
		</HeaderGap>
	);
};

export default function ProductScene() {
	const router = useRouter();
	const { productId } = router.query;

	const content = useMemo(() => {
		if (!productId) return null;

		return <ProductSceneContent productId={Number(productId)} />;
	}, [productId]);

	return <>{content}</>;
}
